/**
 * The account the wizard is building, read back from the API.
 *
 * Three of the five steps need it — the terms step prints it, the seats step counts
 * against it, the package step prices in its tier and currency — and none of them may
 * carry it in memory: `?step=` and `?client=` are the whole of the wizard's state, so
 * a reload on step four has a slug and nothing else. `clients_get` is that read, and
 * it is one hook rather than three because the parse is the part worth having once.
 *
 * **Parsed, not cast.** `useStudioRead` types its answer from the RPC client, which is
 * the handler's return type rather than the schema's; parsing against the shape the
 * action publishes is what turns a renamed field into a failure instead of an
 * `undefined` in a `Kv`.
 */

import { ClientSchema, InvitationSchema, MemberSchema } from '@fareast/shared'
import type { UseQueryResult } from '@tanstack/react-query'
import { z } from 'zod'

import type { ApiError } from '../../../api/hooks.js'
import { useStudioRead } from '../../../api/studio.js'

/** `clients_get`'s output, as `modules/clients/actions.ts` declares it. */
export const AccountSchema = z.object({
  client: ClientSchema,
  members: z.array(MemberSchema),
  invitations: z.array(InvitationSchema),
  seats_used: z.int(),
})

export type AccountView = z.infer<typeof AccountSchema>

export type AccountQuery = {
  /** Null while the read is in flight. */
  readonly account: AccountView | null
  readonly query: UseQueryResult<unknown, ApiError>
}

export function useAccount(client: string): AccountQuery {
  const query = useStudioRead('clients_get', { client })
  return {
    account: query.data === undefined ? null : AccountSchema.parse(query.data),
    query,
  }
}
